import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../api/client";
import { useAuthStore } from "../store/authStore";
import { useSessionStore } from "../store/sessionStore";
import { buildAccessLoginUrl, buildAccessLogoutUrl } from "../config";

export default function HomePage() {
  const navigate = useNavigate();
  const { me, loading, error, setMe, setLoading, setError } = useAuthStore();
  const [title, setTitle] = useState("Untitled Session");
  const [expiresInHours, setExpiresInHours] = useState(8);
  const [creating, setCreating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const canCreate = Boolean(me) && (me?.smokeMode === true || (me?.role === "OWNER" && me?.status === "APPROVED"));
  const needsAccessLogin = useMemo(() => {
    if (!error) return false;
    const msg = String(error).toLowerCase();
    // When Access redirects an XHR/fetch to an HTML login page, browsers often surface it as a generic network error.
    return (
      msg.includes("sign in required") ||
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("load failed") ||
      msg.includes("fetch failed")
    );
  }, [error]);
  const statusLabel = useMemo(() => {
    if (!me) return "";
    return `${me.role} / ${me.status}`;
  }, [me]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .me()
      .then((res) => {
        if (cancelled) return;
        setMe(res);
        // Allow future auto-redirects if the user signs out.
        try {
          sessionStorage.removeItem("jamroom_access_redirected");
          sessionStorage.removeItem("jamroom_auth_error");
        } catch {
          // ignore
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load identity");
        try {
          const stored = sessionStorage.getItem("jamroom_auth_error");
          setAuthError(stored || null);
        } catch {
          setAuthError(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setError, setLoading, setMe]);

  useEffect(() => {
    if (!needsAccessLogin) return;
    if (typeof window === "undefined") return;
    // Avoid infinite loops if Access is misconfigured or user is denied.
    const key = "jamroom_access_redirected";
    if (sessionStorage.getItem(key) === "1") return;
    sessionStorage.setItem(key, "1");
    window.location.assign(buildAccessLoginUrl(window.location.href));
  }, [needsAccessLogin]);

  const onCreate = async () => {
    if (!canCreate) return;
    try {
      setCreating(true);
      const base = new Blob([], { type: "audio/wav" });
      const res = await apiClient.createSession(base, title || "Untitled Session", { expiresInHours });
      if (res.ownerToken) {
        useSessionStore.getState().setOwnerToken(res.ownerToken);
      }
      navigate(`/s/${encodeURIComponent(res.sessionId)}`);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to create session");
    } finally {
      setCreating(false);
    }
  };

  const onSignOut = () => {
    try {
      sessionStorage.removeItem("jamroom_access_redirected");
      sessionStorage.removeItem("jamroom_app_token");
      sessionStorage.removeItem("jamroom_auth_error");
    } catch {
      // ignore
    }
    window.location.assign(buildAccessLogoutUrl(window.location.href));
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "radial-gradient(circle at top, #283247 0%, #151922 48%, #0f1118 100%)",
        color: "#e2e8f3",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "min(720px, 100%)",
          borderRadius: 14,
          border: "1px solid #3a4660",
          background: "#1c2331",
          padding: 24,
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          display: "grid",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/branding/jamroom-mark-180.png"
              alt=""
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: 14,
                boxShadow: "0 14px 36px rgba(0, 212, 255, 0.12), 0 14px 36px rgba(255, 43, 214, 0.1)",
              }}
            />
            <img
              src="/branding/jamroom-wordmark.png"
              alt="JamRoom"
              style={{
                height: 34,
                width: "auto",
                filter: "drop-shadow(0 18px 48px rgba(0, 212, 255, 0.14))",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <div style={{ fontSize: 12, color: "#b9c5db" }}>{loading ? "Checking Access..." : statusLabel || "Unknown"}</div>
            {me && (
              <button
                type="button"
                onClick={onSignOut}
                className="jr-btn jr-btn--ghost"
              >
                Sign out
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ color: "#ffb3b3" }}>
              {needsAccessLogin ? "Sign in required to use the app." : error}
            </div>
            {needsAccessLogin && (
              <div style={{ padding: 12, border: "1px solid #5a4721", borderRadius: 10, background: "#2a2417" }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Cloudflare Access login required</div>
                <div style={{ color: "#d9c69c", fontSize: 13, marginBottom: 10 }}>
                  If you are not redirected automatically, click below to sign in.
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => window.location.assign(buildAccessLoginUrl(window.location.href))}
                    className="jr-btn jr-btn--primary"
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={onSignOut}
                    className="jr-btn jr-btn--ghost"
                  >
                    Sign out
                  </button>
                </div>
                <div style={{ color: "#b9a77f", fontSize: 12, marginTop: 10 }}>
                  If sign-in loops or the API stays unreachable, enable <code>Options preflight bypass</code> in your Access app.
                </div>
                {authError && (
                  <div style={{ color: "#d9c69c", fontSize: 12, marginTop: 10 }}>
                    Auth error: <code>{authError}</code>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {me && me.status !== "APPROVED" && (
          <div style={{ padding: 12, border: "1px solid #5a4721", borderRadius: 10, background: "#2a2417" }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Pending approval</div>
            <div style={{ color: "#d9c69c", fontSize: 13 }}>
              Your account is <code>{me.status}</code>. Ask the owner to approve you.
            </div>
          </div>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 13, color: "#c6d2e7" }}>Session title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled Session"
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid #495874",
                background: "#121722",
                color: "#e2e8f3",
                padding: "10px 12px",
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#c6d2e7" }}>Session expiry</span>
              <span style={{ fontSize: 12, color: "#b9c5db" }}>
                {expiresInHours}h
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={8}
              step={1}
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(Number(e.target.value || 8))}
              disabled={!canCreate || creating}
              aria-label="Session expiry in hours"
              style={{
                width: "100%",
                accentColor: "#00d4ff",
                cursor: !canCreate || creating ? "not-allowed" : "pointer",
              }}
            />
            <div style={{ fontSize: 12, color: "#b9c5db" }}>
              Max 8h. When the session expires, all tracks and uploads are deleted automatically.
            </div>
          </label>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {(me?.smokeMode === true || me?.role === "OWNER") && (
            <button
              type="button"
              onClick={onCreate}
              disabled={!canCreate || creating}
              className="jr-btn jr-btn--primary"
            >
              {creating ? "Creating..." : "Create Room"}
            </button>
          )}

          {me?.role === "OWNER" && (
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="jr-btn jr-btn--ghost"
            >
              Admin
            </button>
          )}
        </div>

        {me && me.smokeMode !== true && me.role !== "OWNER" && (
          <div style={{ fontSize: 12, color: "#b9c5db" }}>
            Rooms can only be created by the owner. You can still join a room via a shared link like{" "}
            <code>/s/&lt;sessionId&gt;</code>.
          </div>
        )}

        <div style={{ fontSize: 12, color: "#b9c5db" }}>
          Tip: open a shared link like <code>/s/&lt;sessionId&gt;</code> to join an existing room.
        </div>
      </div>
    </div>
  );
}
